import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const Performance = () => {
  const [userId, setUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('matchPredictor');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [hasPredictions, setHasPredictions] = useState(true);
  const [matchPredictions, setMatchPredictions] = useState([]);
  const [currentGameweek, setCurrentGameweek] = useState(null);
  const [selectedGameweek, setSelectedGameweek] = useState(null);
  const [matchTotalScore, setMatchTotalScore] = useState(0);
  const [matchPredictionsLoading, setMatchPredictionsLoading] = useState(false);
  const [performanceDisplayName, setPerformanceDisplayName] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const uid = searchParams.get('uid');
    const openOnTab = searchParams.get('openOnTab');
    const openOnGameweek = searchParams.get('openOnGameweek');

    if (!uid) {
      navigate('/not-found');
      return;
    }

    setUserId(uid);
    setActiveTab(openOnTab === 'tablePredictor' ? 'tablePredictor' : 'matchPredictor');

    // Fetch the current gameweek
    const fetchCurrentGameweek = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-gameweek`);
        const currentGw = response.data.gameweek;
        setCurrentGameweek(currentGw);

        // Handle openOnGameweek parameter
        if (openOnGameweek) {
          const parsedGameweek = parseInt(openOnGameweek, 10);
          if (!isNaN(parsedGameweek) && parsedGameweek <= currentGw) {
            setSelectedGameweek(parsedGameweek);
          } else {
            setSelectedGameweek(currentGw);
          }
        } else {
          setSelectedGameweek(currentGw);
        }
      } catch (error) {
        console.error("Error fetching current gameweek:", error);
        setError("Failed to fetch current gameweek. Please try again later.");
      }
    };

    fetchCurrentGameweek();
  }, [location, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;

      try {
        const [currentSeasonResponse, teamsResponse, predictionsResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-season`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/teams`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/table-predictions/live-table`)
        ]);

        const currentSeason = currentSeasonResponse.data.season;

        const processedTeams = teamsResponse.data.map((team, index) => ({
          ...team,
          position: index + 1,
          inputPosition: index + 1
        }));

        const auth = getAuth();
        const user = auth.currentUser;

        if (user) {
          const db = getFirestore();
          const userDocRef = doc(db, 'table_predictions', `${userId}_${currentSeason}`);
          const userDocSnap = await getDoc(userDocRef);

          // Fetch display name
          const usernameDocRef = doc(db, 'users', userId);
          const usernameDocSnap = await getDoc(usernameDocRef);
          if (usernameDocSnap.exists()) {
            setPerformanceDisplayName(usernameDocSnap.data().display_name);
          } else {
            setPerformanceDisplayName('Unknown');
          }

          if (userDocSnap.exists()) {
            const userPredictionsTemp = userDocSnap.data();
            // Keep only key pairs where key begins with 'position_'
            const userPredictions = Object.keys(userPredictionsTemp)
              .filter(key => key.startsWith('position_'))
              .reduce((obj, key) => {
                obj[key] = userPredictionsTemp[key];
                return obj;
              }, {});

            const predictedTeams = Object.keys(userPredictions)
              .sort()
              .map(key => userPredictions[key])
              .map((name, index) => {
                const team = processedTeams.find(t => t.name === name);
                return { ...team, position: index + 1, inputPosition: index + 1 };
              });

            const actualTeams = predictionsResponse.data.map(team => ({
              ...team,
              actualPosition: team.position
            }));

            const combinedTeams = predictedTeams.map(predictedTeam => {
              const actualTeam = actualTeams.find(team => team.teamId === predictedTeam.id);
              const difference = predictedTeam.inputPosition - actualTeam.actualPosition;
              const score = 20 - Math.abs(difference);
              return {
                ...predictedTeam,
                actualPosition: actualTeam.actualPosition,
                difference,
                score
              };
            });

            setTeams(combinedTeams);
            setTotalScore(combinedTeams.reduce((acc, team) => acc + team.score, 0));
            setHasPredictions(true);
          } else {
            setTeams(processedTeams);
            setHasPredictions(false);
          }
        } else {
          setTeams(processedTeams);
          setHasPredictions(false);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('The Fantasy Premier League API is updating, please come back soon.');
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  useEffect(() => {
    const fetchMatchPredictions = async () => {
      if (selectedGameweek === null || !userId) return;
      setMatchPredictionsLoading(true);
    
      try {
        const auth = getAuth();
        const user = auth.currentUser;
    
        if (user) {
          const db = getFirestore();
          const seasonResponse = await axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-season`);
          const seasonValue = seasonResponse.data.season;
    
          const userDocRef = doc(db, 'match_predictions', `${userId}_${seasonValue}_${selectedGameweek}`);
          const userDocSnap = await getDoc(userDocRef);
    
          const [fixturesResponse, teamsResponse] = await Promise.all([
            axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/fixtures/${selectedGameweek}`),
            axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/teams`)
          ]);
          
          const fixtures = fixturesResponse.data;
          const teams = teamsResponse.data;
    
          let predictions = [];
          let totalScore = 0;

          if (userDocSnap.exists()) {
            const userPredictions = userDocSnap.data().predictions;
            predictions = fixtures.map(fixture => {
              const prediction = userPredictions.find(pred => pred.id === fixture.code);
              const score = calculateScore(fixture, prediction);
              totalScore += score;
              const homeTeam = teams.find(team => team.id === fixture.team_h);
              const awayTeam = teams.find(team => team.id === fixture.team_a);
              if (!homeTeam || !awayTeam) {
                console.error('Team not found:', fixture.homeTeam.id, fixture.awayTeam.id);
              }
              return {
                ...fixture,
                homeTeam: homeTeam || { shortName: 'Unknown', logo: '' },
                awayTeam: awayTeam || { shortName: 'Unknown', logo: '' },
                prediction: prediction || { homeScore: '-', awayScore: '-' },
                score
              };
            });
          } else {
            predictions = fixtures.map(fixture => {
              const homeTeam = teams.find(team => team.id === fixture.team_h);
              const awayTeam = teams.find(team => team.id === fixture.team_a);
              if (!homeTeam || !awayTeam) {
                console.error('Teams not found');
              }
              return {
                ...fixture,
                homeTeam: homeTeam || { shortName: 'Unknown', logo: '' },
                awayTeam: awayTeam || { shortName: 'Unknown', logo: '' },
                prediction: { homeScore: '-', awayScore: '-' },
                score: 0
              };
            });
          }
    
          setMatchPredictions(predictions);
          setMatchTotalScore(totalScore);
        }
      } catch (err) {
        console.error('Error fetching match predictions:', err);
        setError('The Fantasy Premier League API is updating, please come back soon.');
      } finally {
        setMatchPredictionsLoading(false);
      }
    };

    fetchMatchPredictions();
  }, [selectedGameweek, userId]);

  const calculateScore = (fixture, prediction) => {
    if (!prediction) return 0;
    if (fixture.started === false) return 0;

    try {
      var actualHome = parseInt(fixture.team_h_score);
      var actualAway = parseInt(fixture.team_a_score);
      var predictedHome = parseInt(prediction.homeScore);
      var predictedAway = parseInt(prediction.awayScore);
    } catch (err) {
      return 0;
    }

    if (actualHome === predictedHome && actualAway === predictedAway) return 7;
    if ((actualHome > actualAway && predictedHome > predictedAway) ||
        (actualHome < actualAway && predictedHome < predictedAway) ||
        (actualHome === actualAway && predictedHome === predictedAway)) {
      if (actualHome === predictedHome || actualAway === predictedAway) return 4;
      return 2;
    }
    if (actualHome === predictedHome || actualAway === predictedAway) return 1;
    return 0;
  };

  const getDifferenceColor = (difference) => {
    if (difference > 0) return 'text-green-500';
    if (difference < 0) return 'text-red-500';
    return 'text-gray-500';
  };

  const formatDifference = (difference) => {
    if (difference > 0) return `+${difference}`;
    return difference;
  };

  if (loading || performanceDisplayName === null) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-red-500 mb-4"></div>
        <div className="text-center text-red-500 text-xl font-bold">{error}</div>
      </div>
    );
  }

  if (performanceDisplayName === 'Unknown') {
    navigate('/not-found');
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-3xl"
    >
      <h1 className="text-4xl font-bold mb-6 text-premier-purple text-center">Performance: {performanceDisplayName}</h1>
      <p className="mt-2 text-gray-700 text-center mb-6">Visit the <Link to="/scoring-guide" className="text-premier-purple font-bold underline hover:text-premier-pink">Scoring Guide</Link> for a breakdown of how points are scored.</p>

      <div className="mb-8 bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-center mb-4">
          <button
            onClick={() => setActiveTab('matchPredictor')}
            className={`px-4 py-2 rounded-l-lg ${activeTab === 'matchPredictor' ? 'bg-premier-purple text-white' : 'bg-gray-200'}`}
          >
            Match Predictor
          </button>
          <button
            onClick={() => setActiveTab('tablePredictor')}
            className={`px-4 py-2 rounded-r-lg ${activeTab === 'tablePredictor' ? 'bg-premier-purple text-white' : 'bg-gray-200'}`}
          >
            Table Predictor
          </button>
        </div>

        {activeTab === 'matchPredictor' && (
          <div>
            {currentGameweek !== null && (
              <div className="mb-4 flex justify-center items-center">
                <button
                  onClick={() => {
                    setSelectedGameweek(prev => Math.max(1, prev - 1));
                    setMatchPredictionsLoading(true);
                  }}
                  className="px-3 py-2 bg-premier-purple text-white rounded-l transition duration-300 ease-in-out hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
                  disabled={selectedGameweek <= 1 || matchPredictionsLoading}
                >
                  <ChevronLeft />
                </button>
                <span className="px-6 py-2 bg-gray-100 text-gray-800 font-semibold text-lg">
                  Gameweek {selectedGameweek}
                </span>
                <button
                  onClick={() => {
                    setSelectedGameweek(prev => Math.min(currentGameweek, prev + 1));
                    setMatchPredictionsLoading(true);
                  }}
                  className="px-3 py-2 bg-premier-purple text-white rounded-r transition duration-300 ease-in-out hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
                  disabled={selectedGameweek >= currentGameweek || matchPredictionsLoading}
                >
                  <ChevronRight />
                </button>
              </div>
            )}
            {matchPredictionsLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-premier-purple"></div>
              </div>
            ) : matchPredictions.length > 0 ? (
              <>
                <div className="mb-4 p-4 rounded-lg text-center text-white bg-green-500 text-lg font-bold">
                  Gameweek Score: {matchTotalScore}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {matchPredictions.map(match => (
                    <motion.div
                      key={match.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white rounded-lg shadow-md p-4"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">
                          {(() => {
                            const date = new Date(match.kickoff_time).toLocaleDateString();
                            if (match.finished_provisional) {
                              return `${date}: Full-Time`;
                            } else if (match.started && !match.finished_provisional) {
                              return `${date}: Live (${match.minutes}')`;
                            } else {
                              return `${date} - Yet to Kickoff`;
                            }
                          })()}
                        </span>
                        <span className="text-sm font-bold text-premier-purple">
                          Points: {match.score}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <img src={match.homeTeam.logo} alt={match.homeTeam.shortName} className="w-8 h-8 mr-2 p-0.5 bg-gray-400 rounded-full flex items-center justify-center overflow-hidden" />
                          <span>{match.homeTeam.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="font-bold">{match.team_h_score ?? '-'}</span>
                          <span className="text-gray-500">({match.prediction.homeScore})</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center">
                          <img src={match.awayTeam.logo} alt={match.awayTeam.shortName} className="w-8 h-8 mr-2 p-0.5 bg-gray-400 rounded-full flex items-center justify-center overflow-hidden" />
                          <span>{match.awayTeam.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="font-bold">{match.team_a_score ?? '-'}</span>
                          <span className="text-gray-500">({match.prediction.awayScore})</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center text-xl text-gray-600 py-8">
                {currentGameweek === 0 ? (
                  "Check back when the season begins!"
                ) : (
                  "No match predictions available for this gameweek."
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tablePredictor' && (
          <div>
            {currentGameweek === 0 ? (
              <div className="text-center text-xl text-gray-600 py-8">
                Check back when the season begins!
              </div>
            ) : hasPredictions ? (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mb-4 p-4 rounded-lg text-center text-white bg-green-500 text-lg font-bold"
                >
                  Live Score: {totalScore} / 400
                </motion.div>
                <div className="hidden md:block">
                  <motion.table
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="w-full bg-white rounded-lg shadow-md"
                  >
                    <thead>
                      <tr className="bg-premier-purple text-white">
                        <th className="py-2 px-4 text-center">Team</th>
                        <th className="py-2 px-4 text-center">Predicted Position</th>
                        <th className="py-2 px-4 text-center">Current Position</th>
                        <th className="py-2 px-4 text-center">Difference</th>
                        <th className="py-2 px-4 text-center">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teams.map((team, index) => (
                        <motion.tr
                          key={team.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                          className="border-b"
                        >
                          <td className="py-2 px-4 text-center">{team.name}</td>
                          <td className="py-2 px-4 text-center">{team.inputPosition}</td>
                          <td className="py-2 px-4 text-center">{team.actualPosition}</td>
                          <td className={`py-2 px-4 text-center ${getDifferenceColor(team.difference)}`}>
                            {formatDifference(team.difference)}
                          </td>
                          <td className="py-2 px-4 text-center">{team.score}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </motion.table>
                </div>
                <div className="md:hidden">
                  {teams.map((team, index) => (
                    <motion.div
                      key={team.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                      className="bg-white rounded-lg shadow-md p-4 mb-4"
                    >
                      <h3 className="font-bold text-lg mb-2">{team.name}</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-sm text-gray-600">Predicted Position</p>
                          <p className="font-bold">{team.inputPosition}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Current Position</p>
                          <p className="font-bold">{team.actualPosition}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Difference</p>
                          <p className={`font-bold ${getDifferenceColor(team.difference)}`}>
                            {formatDifference(team.difference)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Score</p>
                          <p className="font-bold">{team.score}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center text-xl text-gray-600 py-8"
              >
                No table predictions are available.
              </motion.div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Performance;